import type { PriceListItem } from '@/types'
import { formatDate as formatStyledDate } from '@/utils/dateFormat'

/**
 * Delivery-day and date helpers shared by ChangeItemsModal (quantity edits)
 * and AddProductModal (the add-product wizard).
 *
 * Day numbering is 0=Sunday..6=Saturday throughout, matching JavaScript's
 * Date.getDay() and the CRM's delivery_day values.
 */

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

const DAY_KEYS: Record<number, keyof PriceListItem['chargebee_ids']> = {
  1: 'monday',
  2: 'tuesday',
  3: 'wednesday',
  4: 'thursday',
  5: 'friday',
  6: 'saturday',
  0: 'sunday',
}

/** Today + N calendar days as a YYYY-MM-DD string (local time). */
export function getDateDaysFromNow(days: number): string {
  const date = new Date()
  date.setDate(date.getDate() + days)
  return toLocalDateString(date)
}

/**
 * A Date as a YYYY-MM-DD string in LOCAL time. Use this instead of
 * `date.toISOString().split('T')[0]`, which formats in UTC and so lands on the
 * wrong calendar day (and weekday) for any non-UTC timezone. Delivery dates are
 * built at local midnight and matched by local getDay(), so they must be
 * stringified in local time to stay consistent.
 */
export function toLocalDateString(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

/** Add N days to a YYYY-MM-DD string, returning a YYYY-MM-DD string (local time). */
export function addDaysToDateString(dateString: string, days: number): string {
  const [year, month, day] = dateString.split('-').map(Number)
  return toLocalDateString(new Date(year, month - 1, day + days))
}

/** "Mon, 8 Jun 2026" from a YYYY-MM-DD string. */
export function formatDate(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

/** Weekday name from a 0..6 number. */
export function getDayName(dayNumber: number): string {
  return DAY_NAMES[dayNumber] || 'Unknown'
}

/**
 * "Mon 21st Jun" from a YYYY-MM-DD string, or the long form, depending on
 * DATE_STYLE. Kept as a re-export so the many call sites reading dates out of
 * the API do not each have to know where the formatter lives.
 */
export function formatOrdinalDate(dateString: string): string {
  return formatStyledDate(dateString)
}

/**
 * Weekday (0=Sunday..6=Saturday) of a YYYY-MM-DD string, parsed in local time.
 * Built from explicit parts rather than `new Date(dateString)` because the
 * latter parses YYYY-MM-DD as UTC midnight, which getDay() can report as the
 * previous day in timezones behind UTC.
 */
export function getWeekdayFromDateString(dateString: string): number {
  const [year, month, day] = dateString.split('-').map(Number)
  return new Date(year, month - 1, day).getDay()
}

/** The chargebee id for a product on a given weekday, or null if not deliverable. */
export function getChargebeeIdForDay(item: PriceListItem, dayNumber: number): string | null {
  const key = DAY_KEYS[dayNumber]
  return key ? item.chargebee_ids[key] : null
}

/**
 * The weekdays a product can actually reach a given customer: the days where
 * the product is deliverable (has a chargebee id) AND the customer takes a
 * delivery. Sorted ascending. e.g. a Thu/Fri product for a Tue/Fri customer
 * resolves to [5] (Friday).
 */
export function getDeliverableDays(item: PriceListItem, customerDeliveryDays: number[]): number[] {
  return [...customerDeliveryDays]
    .filter(day => getChargebeeIdForDay(item, day) !== null)
    .sort((a, b) => a - b)
}

/** An eligible one-off delivery date (milk lockers, DF-230). */
export interface OneOffEligibleDate {
  date: string
  day: number
  chargebeeId: string
}

/**
 * The dates a one-off (milk locker, DF-230) can be delivered on: the customer's
 * own delivery weekdays, from the add_new_line lead-time floor onward, that the
 * product can reach and that isn't a booked holiday. Shared by the add flow
 * (ProductLandingPage) and the move-on-pause prompt (SunDashboard). `isOnHoliday`
 * skips booked holidays; `exclude` drops any other date the caller doesn't want
 * offered (e.g. the day being paused).
 */
export function computeOneOffEligibleDates(params: {
  item: PriceListItem | null
  customerDeliveryDays: number[]
  floorDate: string | null | undefined
  horizonDays: number
  isOnHoliday?: (dateStr: string) => boolean
  exclude?: (dateStr: string) => boolean
}): OneOffEligibleDate[] {
  const { item, customerDeliveryDays, floorDate, horizonDays, isOnHoliday, exclude } = params
  if (!item || customerDeliveryDays.length === 0 || !floorDate) return []
  const out: OneOffEligibleDate[] = []
  for (let i = 0; i <= horizonDays; i++) {
    const date = addDaysToDateString(floorDate, i)
    const day = getWeekdayFromDateString(date)
    if (!customerDeliveryDays.includes(day)) continue
    const chargebeeId = getChargebeeIdForDay(item, day)
    if (!chargebeeId) continue
    if (isOnHoliday?.(date)) continue
    if (exclude?.(date)) continue
    out.push({ date, day, chargebeeId })
  }
  return out
}

/** Human list of day names: "Friday" or "Monday or Tuesday" or "Mon, Tue or Wed". */
export function formatDayList(days: number[]): string {
  const names = days.map(getDayName)
  if (names.length === 0) return ''
  if (names.length === 1) return names[0]
  return `${names.slice(0, -1).join(', ')} or ${names[names.length - 1]}`
}
